import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListPlus } from "lucide-react";
import api, { apiError, uploadPhoto } from "@/lib/api";
import { eur, fmtDate, Loading, StatusBadge, EmptyState } from "@/components/common";
import Section from "@/components/Section";

const STATUS_OPTIONS = ["nuova", "presa_in_carico", "programmata", "completata"];

function AdminComplete({ item, onDone }) {
  const [photo, setPhoto] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      setPhoto(await uploadPhoto(f));
      toast.success("Foto caricata.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!photo) return;
    setBusy(true);
    try {
      await api.post(`/admin/interventions/${item.id}/complete`, { final_photo: photo, notes });
      toast.success("Servizio completato: la foto è ora visibile al cliente.");
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-[#f1f5f9] pt-3 space-y-2">
      <label className="label-soft">Foto del servizio effettuato (obbligatoria per completare)</label>
      <input type="file" accept="image/*" data-testid={`admin-photo-${item.id}`} onChange={onFile} className="text-sm block" />
      {photo && <img src={photo} alt="Anteprima foto" className="w-24 rounded-lg" />}
      <input className="input-soft text-sm" placeholder="Note (facoltative)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`admin-notes-${item.id}`} />
      <button data-testid={`admin-complete-${item.id}`} disabled={!photo || busy} onClick={complete} className="btn-primary !py-2 !px-4 text-sm min-h-[44px] disabled:opacity-50">
        Servizio completato
      </button>
    </div>
  );
}

export default function AdminInterventi({ preset }) {
  const [items, setItems] = useState(null);
  const [territories, setTerritories] = useState([]);
  const [cemeteries, setCemeteries] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCem, setFilterCem] = useState("");
  const [planDate, setPlanDate] = useState("");
  const [planning, setPlanning] = useState(null);

  const load = () => {
    const q = new URLSearchParams();
    if (filterStatus) q.set("status", filterStatus);
    if (filterCem) q.set("cemetery_id", filterCem);
    api.get(`/admin/interventions?${q}`).then((r) => setItems(r.data));
  };

  useEffect(() => {
    api.get("/admin/cemeteries").then((r) => setCemeteries(r.data));
    api.get("/admin/territories").then((r) => setTerritories(r.data));
  }, []);
  useEffect(load, [filterStatus, filterCem]);

  useEffect(() => {
    if (!preset) return;
    setFilterStatus(preset.status || "");
    setFilterCem("");
  }, [preset]);

  useEffect(() => {
    if (items && preset?.focus) {
      document.querySelector(`[data-testid="admin-intervention-${preset.focus}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [items, preset]);

  const proFor = (i) => territories.find((t) => t.comune === i.cemetery?.comune)?.pro_name;

  const approve = async (id) => {
    try {
      const r = await api.post(`/admin/interventions/${id}/approve`);
      toast.success(`Intervento assegnato a ${r.data.professional_name}.`);
      load();
      loadPlanning();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/interventions/${id}`, body);
      toast.success("Intervento aggiornato.");
      load();
      loadPlanning();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadPlanning = () => {
    if (!planDate) { setPlanning(null); return; }
    api.get(`/admin/planning?date=${planDate}`).then((r) => setPlanning(r.data));
  };
  useEffect(loadPlanning, [planDate]);

  if (!items) return <Loading />;

  const renderItem = (i) => (
    <div key={i.id} data-testid={`admin-intervention-${i.id}`} className="card-soft p-5 space-y-3">
      <div className="flex justify-between gap-4 flex-wrap">
        <div>
          <p className="font-semibold text-navy">
            {i.service}{i.flower_name ? ` · ${i.flower_name}` : ""}
            {i.is_trial && <span className="ml-2 text-xs font-bold text-[#4a7c59]">PROVA</span>}
          </p>
          <p className="text-sm text-muted-soft">
            {i.customer_name} · {i.cemetery?.name} · {i.grave ? `${i.grave.deceased_name} ${i.grave.deceased_surname} (campo ${i.grave.campo || "—"}, fila ${i.grave.fila || "—"}, n. ${i.grave.numero || "—"})` : ""}
          </p>
          <p className="text-xs text-muted-soft mt-0.5">
            {i.order_type} · {i.price > 0 ? eur(i.price) : "incluso"} · richiesto il {fmtDate(i.created_at)}
          </p>
        </div>
        <StatusBadge status={i.status} />
      </div>
      {i.status !== "completata" && (
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <label className="label-soft">Professionista</label>
            {i.professional_id ? (
              <p className="text-sm text-navy font-medium py-2" data-testid={`assigned-pro-${i.id}`}>{i.professional_name} · esclusiva {i.cemetery?.comune}</p>
            ) : (
              <div className="space-y-1.5">
                <button data-testid={`approve-${i.id}`} onClick={() => approve(i.id)} className="btn-primary !py-2 !px-4 text-sm min-h-[44px]">Approva e assegna</button>
                <p className="text-xs text-muted-soft">
                  {proFor(i) ? `Verrà assegnato a ${proFor(i)} (esclusiva ${i.cemetery?.comune})` : `Nessun professionista assegnato al comune ${i.cemetery?.comune || ""}`}
                </p>
              </div>
            )}
          </div>
          <div>
            <label className="label-soft">Data</label>
            <input
              data-testid={`schedule-date-${i.id}`}
              type="date"
              className="input-soft !w-auto !py-2 text-sm min-h-[44px]"
              defaultValue={i.scheduled_date || ""}
              onBlur={(e) => e.target.value && e.target.value !== i.scheduled_date && patch(i.id, { scheduled_date: e.target.value })}
            />
          </div>
        </div>
      )}
      {i.status !== "completata" && i.status !== "annullata" && <AdminComplete item={i} onDone={load} />}
      {i.final_photo && (
        <img src={i.final_photo} alt="Foto finale" className="w-32 rounded-lg" />
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-interventi">
      <section>
        <h2 className="font-serif text-2xl text-navy mb-4">Interventi</h2>
        <div className="flex gap-3 flex-wrap mb-5">
          <select data-testid="filter-status" className="input-soft !w-auto min-h-[44px]" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">Tutti gli stati</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select data-testid="filter-cemetery" className="input-soft !w-auto min-h-[44px]" value={filterCem} onChange={(e) => setFilterCem(e.target.value)}>
            <option value="">Tutti i cimiteri</option>
            {cemeteries.map((c) => <option key={c.id} value={c.id}>{`${c.name} — ${c.address}`}</option>)}
          </select>
        </div>
        {items.length === 0 ? (
          <EmptyState title="Nessun intervento trovato" />
        ) : (
          <div className="space-y-3">
            {items.slice(0, 5).map(renderItem)}
            {items.length > 5 && (
              <Section title="Tutti gli altri interventi" count={items.length - 5} icon={ListPlus}
                testid="admin-interventi-more" defaultOpen={!!preset?.focus}>
                <div className="space-y-3">{items.slice(5).map(renderItem)}</div>
              </Section>
            )}
          </div>
        )}
      </section>

      <section data-testid="admin-planning">
        <h2 className="font-serif text-2xl text-navy mb-2">Programmazione per giornata</h2>
        <p className="text-sm text-muted-soft mb-4">
          Gli interventi sono raggruppati per cimitero; l'ordine suggerito parte dall'ingresso e segue la posizione dei luoghi. Puoi modificarlo con le frecce.
        </p>
        <input data-testid="planning-date" type="date" className="input-soft !w-auto min-h-[44px] mb-5" value={planDate} onChange={(e) => setPlanDate(e.target.value)} />
        {planning && (
          Object.keys(planning).length === 0 ? (
            <EmptyState title="Nessun intervento in questa giornata" />
          ) : (
            <div className="space-y-6">
              {Object.entries(planning).map(([cid, group]) => (
                <div key={cid} className="card-soft p-6" data-testid={`planning-group-${cid}`}>
                  <p className="font-semibold text-navy mb-3">{group.cemetery?.name || "Senza cimitero"}</p>
                  <div className="space-y-2">
                    {group.items.map((d, idx) => (
                      <div key={d.id} className="flex items-center gap-3 text-sm border border-[#f1f5f9] rounded-xl px-4 py-3" data-testid={`planning-item-${d.id}`}>
                        <span className="font-serif text-lg text-[#4a7c59] w-6">{idx + 1}</span>
                        <div className="flex-1">
                          <p className="text-navy font-medium">{d.grave ? `${d.grave.deceased_name} ${d.grave.deceased_surname}` : "—"} · {d.service}</p>
                          <p className="text-xs text-muted-soft">{d.customer_name} · campo {d.grave?.campo || "—"}, fila {d.grave?.fila || "—"}, n. {d.grave?.numero || "—"}</p>
                        </div>
                        <StatusBadge status={d.status} />
                        <div className="flex flex-col">
                          <button data-testid={`move-up-${d.id}`} disabled={idx === 0} onClick={() => patch(d.id, { order_index: idx - 1 })} className="text-xs text-[#1e3a5f] disabled:opacity-30 min-w-[44px] min-h-[22px]">▲</button>
                          <button data-testid={`move-down-${d.id}`} disabled={idx === group.items.length - 1} onClick={() => patch(d.id, { order_index: idx + 1 })} className="text-xs text-[#1e3a5f] disabled:opacity-30 min-w-[44px] min-h-[22px]">▼</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </section>
    </div>
  );
}
